import {useState} from 'react'
import {useIntl} from 'react-intl'
import {createPortal} from 'react-dom'
import {Modal} from 'react-bootstrap'
import {defaultCreateAppData, ICreateAppData} from './IAppModels'
import {KTIcon} from '../../../helpers'
import {Step1} from './steps/Step1'
import {readClaimCurrency} from './claim-currency'
import {useAuth} from '../../../../app/modules/auth'
import {toast} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

type Props = {
  show: boolean
  handleClose: () => void
  claimDetails: any
  formattedDate: string
  onSuccessSubmit: () => void
}

const modalsRoot = document.getElementById('root-modals') || document.body

const FALLBACK_ERROR = 'Failed to update status to Request FGL'

/**
 * Pulls a human-readable message out of a failed UpdateStatus response.
 *
 * The endpoint can fail in three shapes: a GeneralResponses body with `message` (a rejected
 * validation guard, returned as 500), a bare JSON string (an unhandled exception), or ASP.NET
 * ProblemDetails with `errors` (model binding — e.g. a formatted string sent to a decimal field).
 */
const readErrorMessage = async (response: Response): Promise<string> => {
  try {
    const body = await response.json()

    if (typeof body === 'string') return body || FALLBACK_ERROR
    if (typeof body?.message === 'string' && body.message) return body.message

    if (body?.errors && typeof body.errors === 'object') {
      const details = Object.values(body.errors).flat().join(' ')
      if (details) return details
    }

    if (typeof body?.title === 'string' && body.title) return body.title
  } catch {
    // Body was empty or not JSON — fall through to the generic message.
  }

  return FALLBACK_ERROR
}

const CreateAppModal = ({
  show,
  handleClose,
  claimDetails,
  formattedDate,
  onSuccessSubmit,
}: Props) => {
  const {formatMessage: f} = useIntl()
  const user = useAuth()
  const {auth} = useAuth()
  const [data, setData] = useState<ICreateAppData>(defaultCreateAppData)
  const [loading, setLoading] = useState(false)
  const [dischargeDate, setDischargeDate] = useState<string>('')
  // The raw numeric amount the provider expects to bill (IDH-1285). Null until they enter a valid
  // figure. Step1 owns the formatted display; only this number is ever posted.
  const [providerClaimAmount, setProviderClaimAmount] = useState<number | null>(null)

  // Add state to track files for each category
  const [fileCategories, setFileCategories] = useState({
    resumeMedis: [],
    billingAkhir: [],
    hasilPenunjang: [],
  })

  // Check if all required categories have at least one file
  const hasAllRequiredFiles = Object.values(fileCategories).every((files) => files.length > 0)

  const hasClaimAmount = providerClaimAmount !== null && providerClaimAmount > 0
  const canSubmit = hasAllRequiredFiles && hasClaimAmount

  const updateData = (fieldsToUpdate: Partial<ICreateAppData>) => {
    const updatedData = {...data, ...fieldsToUpdate}
    setData(updatedData)
  }

  const handleFileUpload = (category: string, files: File[]) => {
    // Instead of appending, we replace the entire array for the category
    setFileCategories((prev) => ({
      ...prev,
      [category]: files,
    }))
  }

  const handleDischargeDate = (date: string) => {
    setDischargeDate(date)
  }

  const submit = async () => {
    if (!hasAllRequiredFiles) {
      toast.error('Please upload at least one file for each category')
      return
    }

    if (!hasClaimAmount) {
      toast.error('Please enter the Claim Amount you expect to bill')
      return
    }

    setLoading(true)
    try {
      // Rest of the status update code remains the same
      const statusChangeResponse = await fetch(
        `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/UpdateStatus`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
          },
          body: JSON.stringify({
            claimNumber: claimDetails.claimDetails.claim.claimNumber,
            status: 'Request FGL',
            username: user.currentUser?.email,
            source: 'Provider',
            dischargeDate: dischargeDate,
            // A raw number, never a formatted string: the API field is decimal? and a formatted
            // string would fail model binding and 400 the whole status change.
            providerClaimAmount: providerClaimAmount,
          }),
        }
      )

      if (!statusChangeResponse.ok) {
        // Surface the server's own message. A provider on a stale tab needs to know what to fix
        // while a patient is waiting to be discharged — a generic failure tells them nothing.
        throw new Error(await readErrorMessage(statusChangeResponse))
      }

      toast.success('FGL Request submitted successfully!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })

      sessionStorage.removeItem('recentlyUploadedLabels')
      onSuccessSubmit()
      handleClose()
    } catch (error) {
      console.error('Error:', error)
      toast.error('Error: ' + (error as Error).message)
    } finally {
      setLoading(false)
    }
  }

  return createPortal(
    <Modal
      data-testid='create-app-modal'
      id='kt_modal_create_app'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered mw-1000px'
      show={show}
      onHide={handleClose}
      backdrop={true}
    >
      <div
        className='modal-header'
        style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
      >
        <h2>Request FGL</h2>
        <div
          className='btn btn-sm btn-icon btn-active-color-primary'
          onClick={handleClose}
          style={{
            marginLeft: 'auto', // This pushes the button to the right
            cursor: 'pointer',
          }}
        >
          <KTIcon className='fs-1' iconName='cross' />
        </div>
      </div>

      <div className='modal-body py-lg-10 px-lg-10'>
        <div className='row'>
          {/* Left Section - Document Requirements */}
          <div className='col-md-4'>
            <div className='required-docs-section'>
              <h4 className='mb-4'>Required Documents</h4>
              <div className='doc-requirement mb-3'>
                <div className='d-flex align-items-center mb-2'>
                  <span className='bullet bullet-dot bg-primary me-2'></span>
                  <span className='fw-bold'>{f({id: 'CLAIM.INPATIENT.RESUME_MEDIS'})}</span>
                </div>
                <div className='text-muted small'>
                  Files uploaded: {fileCategories.resumeMedis.length}
                </div>
              </div>
              <div className='doc-requirement mb-3'>
                <div className='d-flex align-items-center mb-2'>
                  <span className='bullet bullet-dot bg-primary me-2'></span>
                  <span className='fw-bold'>{f({id: 'CLAIM.INPATIENT.FINAL_BILL'})}</span>
                </div>
                <div className='text-muted small'>
                  Files uploaded: {fileCategories.billingAkhir.length}
                </div>
              </div>
              <div className='doc-requirement mb-3'>
                <div className='d-flex align-items-center mb-2'>
                  <span className='bullet bullet-dot bg-primary me-2'></span>
                  <span className='fw-bold'>{f({id: 'CLAIM.INPATIENT.SUPPORTING_DOCUMENTS'})}</span>
                </div>
                <div className='text-muted small'>
                  Files uploaded: {fileCategories.hasilPenunjang.length}
                </div>
              </div>
            </div>
          </div>

          {/* Right Section - Upload Form */}
          <div className='col-md-8'>
            <form noValidate id='kt_modal_create_app_form'>
              <Step1
                data={data}
                updateData={updateData}
                hasError={false}
                onFileUpload={handleFileUpload}
                fileCategories={fileCategories}
                onDischargeDateChange={handleDischargeDate}
                onClaimAmountChange={setProviderClaimAmount}
                claimNumber={claimDetails.claimDetails.claim.claimNumber}
                currency={readClaimCurrency(claimDetails)}
              />
            </form>
          </div>
        </div>

        {/* Submit button moved outside the form and positioned at the bottom right */}
        <div className='d-flex justify-content-end mt-5'>
          <button
            type='button'
            className='btn btn-lg btn-primary'
            onClick={submit}
            disabled={loading || !canSubmit}
            style={{
              backgroundColor: canSubmit ? '#059BB6' : '#E0E0E0',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              minWidth: '120px', // Ensure button has enough width
            }}
          >
            {loading ? (
              <span>
                <span
                  className='spinner-border spinner-border-sm'
                  role='status'
                  aria-hidden='true'
                ></span>{' '}
                Submitting...
              </span>
            ) : (
              <>
                Submit <KTIcon iconName='arrow-right' className='fs-3 ms-2 me-0' />
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>,
    modalsRoot
  )
}

export {CreateAppModal}
